import { saveAs } from 'file-saver';
import { HttpClient, IAccessTokenStore } from 'Mri.Web.Common';

export interface IConfig {
    method?: string;
    data?: string | {};
    headers?: Headers;
}

export interface IImportModel {
    files: FileList;
}

export class CsvStore {
    public constructor(httpClient: HttpClient, accessTokenStore: IAccessTokenStore) {
        this.httpClient = httpClient;
        this.accessTokenStore = accessTokenStore;
    }

    public async previewImportAsync(model: IImportModel) {
        const headers = new Headers();
        headers.append('Accept', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const formData = new FormData();
        formData.append('file', model.files[0]);
        const response: Response = await fetch(`/api/v1/csv/preview-import`, {
            body: formData,
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });
        const data = await response.json();

        if (response.ok) {
            return data;
        } else if (response.status == 400) {
            throw new Error(data);
        } else {
            throw data;
        }
    }

    public async saveAsync(models: Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel[]) {
        await this.httpClient.post(`/api/v1/csv/save`, models);
    }

    public async exportConfigAsync(model: Mri.Web.Internal.Controllers.Displays.DisplaySerialNumberListModel) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-config`, {
            body: JSON.stringify(model),
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportTelemetryAsync(model: Mri.Web.Internal.Controllers.Csv.ExportTelemetryModel) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-telemetry`, {
            body: JSON.stringify(model),
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportEventsAsync(model: Mri.Web.Internal.Controllers.Csv.ExportEventsModel) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-events`, {
            body: JSON.stringify(model),
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportSoftwareVersionsAsync(
        model: Mri.Web.Internal.Controllers.Displays.DisplaySerialNumberListModel
    ) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-software-versions`, {
            body: JSON.stringify(model),
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportDisplaySoftwareManagerAsync(
        model: Mri.Web.Internal.Controllers.Displays.DisplaySerialNumberListModel
    ) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-software-manager`, {
            body: JSON.stringify(model),
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    private readonly httpClient: HttpClient;
    private readonly accessTokenStore: IAccessTokenStore;
}
